import { useState, useEffect } from 'react';

// Static fallback adventures data (used if database is empty)
const fallbackAdventures = [
  {
    id: 'reef-diving',
    slug: 'reef-diving',
    title: 'Great Barrier Reef Diving',
    short_description: 'Explore the world\'s largest coral reef system with pristine marine life.',
    description: 'Dive into the breathtaking underwater world of the Great Barrier Reef, one of the seven natural wonders of the world. Experience vibrant coral formations, tropical fish, and marine life in crystal-clear waters.',
    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
    image_alt: 'Colorful coral reef underwater with tropical fish',
    features: ['Professional PADI instructors', 'All equipment included', 'Small group sizes', 'Underwater photography'],
    duration: '3 days, 2 nights',
    group_size: '2-6 people',
    price: 'From $450',
    price_num: 450,
    category: 'scuba-diving',
    skill_level: 'beginner',
    location: 'Great Barrier Reef, Australia',
    is_active: true,
    featured: true,
    sort_order: 1
  },
  {
    id: 'wreck-diving',
    slug: 'wreck-diving',
    title: 'Historic Wreck Diving',
    short_description: 'Discover sunken ships and underwater history in protected marine sanctuaries.',
    description: 'Explore fascinating underwater archaeological sites and historic shipwrecks. These underwater time capsules offer a unique glimpse into maritime history while serving as artificial reefs teeming with marine life.',
    image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
    image_alt: 'Underwater shipwreck covered in marine growth',
    features: ['Advanced diving techniques', 'Historical briefings', 'Penetration training', 'Marine archaeology'],
    duration: '2 days',
    group_size: '2-4 people',
    price: 'From $380',
    price_num: 380,
    category: 'scuba-diving',
    skill_level: 'intermediate',
    location: 'Various Historic Sites',
    is_active: true,
    featured: false,
    sort_order: 2
  },
  {
    id: 'night-diving',
    slug: 'night-diving',
    title: 'Night Diving Adventure',
    short_description: 'Experience the mysterious underwater world after dark with specialized lighting.',
    description: 'Discover a completely different underwater ecosystem as nocturnal marine life emerges. Night diving reveals behaviors and creatures rarely seen during daylight hours, creating an unforgettable underwater experience.',
    image: 'https://images.unsplash.com/photo-1582967788606-a171c1080cb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
    image_alt: 'Diver with underwater light exploring coral reef at night',
    features: ['Specialized night diving gear', 'Underwater lighting systems', 'Nocturnal marine life spotting', 'Advanced safety protocols'],
    duration: '1 day',
    group_size: '2-4 people',
    price: 'From $220',
    price_num: 220,
    category: 'scuba-diving',
    skill_level: 'intermediate',
    location: 'Protected Marine Areas',
    is_active: true,
    featured: true,
    sort_order: 3
  }
];

export const useAdventures = () => {
  const [adventures, setAdventures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAdventures();
  }, []);

  const fetchAdventures = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to fetch from database first
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/adventures`);
      
      if (response.ok) {
        const data = await response.json();
        
        // If database has adventures, use them
        if (data && data.length > 0) {
          setAdventures(data);
        } else {
          // No adventures in database
          console.warn('No adventures found in database');
          setAdventures([]);
          setError('No adventures available');
        }
      } else {
        // If API fails, show error
        console.error('API failed with status:', response.status);
        setAdventures([]);
        setError(`Failed to load adventures (${response.status})`);
      }
    } catch (err) {
      console.error('Error fetching adventures:', err);
      setError('Failed to load adventures');
      setAdventures([]);
    } finally {
      setLoading(false);
    }
  };

  const getAdventureBySlug = (slug) => {
    return adventures.find(adventure => adventure.slug === slug);
  };

  const getFeaturedAdventures = () => {
    return adventures.filter(adventure => adventure.featured && adventure.is_active);
  };

  const getAdventuresByCategory = (category) => {
    return adventures.filter(adventure => 
      adventure.category === category && adventure.is_active
    );
  };

  return {
    adventures: adventures.filter(adventure => adventure.is_active),
    loading,
    error,
    refetch: fetchAdventures,
    getAdventureBySlug,
    getFeaturedAdventures,
    getAdventuresByCategory
  };
};

export default useAdventures;